import { logger } from "../utils/logger";
import { TourPersistenceData } from "../types/tour";

export class TourPersistence {
  private static readonly STORAGE_KEY_PREFIX = "connectai_tour";
  private static readonly CURRENT_VERSION = "v1.0.0";

  /**
   * Gera a chave de armazenamento para um usuário específico
   */
  private static getStorageKey(userId?: string): string {
    const userPart = userId ? `_user_${userId}` : "_anonymous";
    return `${this.STORAGE_KEY_PREFIX}${userPart}_${this.CURRENT_VERSION}`;
  }

  /**
   * Salva o estado do tour no localStorage
   */
  public static saveTourState(
    data: TourPersistenceData,
    userId?: string,
  ): void {
    try {
      const key = this.getStorageKey(userId);
      const dataToSave = {
        ...data,
        version: this.CURRENT_VERSION,
        userId,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(key, JSON.stringify(dataToSave));
    } catch (error) {
      logger.error("[TourPersistence] Erro ao salvar estado do tour:", error);
    }
  }

  /**
   * Carrega o estado do tour do localStorage
   */
  public static loadTourState(userId?: string): TourPersistenceData | null {
    try {
      const key = this.getStorageKey(userId);
      const stored = localStorage.getItem(key);

      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored) as TourPersistenceData & {
        lastUpdated?: number;
      };

      // Verificar se a versão é compatível
      if (data.version !== this.CURRENT_VERSION) {
        this.clearTourState(userId); // Limpar dados antigos
        return null;
      }

      return data;
    } catch (error) {
      logger.error("[TourPersistence] Erro ao carregar estado do tour:", error);
      return null;
    }
  }

  /**
   * Marca o tour como completo
   */
  public static markTourCompleted(userId?: string): void {
    const currentState = this.loadTourState(userId) || {
      completed: false,
      version: this.CURRENT_VERSION,
    };

    this.saveTourState(
      {
        ...currentState,
        completed: true,
        completedAt: Date.now(),
      },
      userId,
    );
  }

  /**
   * Marca o tour como pulado
   */
  public static markTourSkipped(userId?: string): void {
    const currentState = this.loadTourState(userId) || {
      completed: false,
      version: this.CURRENT_VERSION,
    };

    this.saveTourState(
      {
        ...currentState,
        completed: false,
        skippedAt: Date.now(),
      },
      userId,
    );
  }

  /**
   * Salva o progresso atual do tour
   */
  public static saveTourProgress(
    currentStep: number,
    completedSteps: string[],
    userId?: string,
  ): void {
    const currentState = this.loadTourState(userId) || {
      completed: false,
      version: this.CURRENT_VERSION,
    };

    this.saveTourState(
      {
        ...currentState,
        currentStep,
        completedSteps,
      },
      userId,
    );
  }

  /**
   * Verifica se o tour foi completado
   */
  public static isTourCompleted(userId?: string): boolean {
    const state = this.loadTourState(userId);
    return state?.completed === true;
  }

  /**
   * Verifica se o tour foi pulado
   */
  public static wasTourSkipped(userId?: string): boolean {
    const state = this.loadTourState(userId);
    return state?.skippedAt !== undefined && !state.completed;
  }

  /**
   * Verifica se o usuário já interagiu com o tour (completo ou pulado)
   */
  public static hasUserInteractedWithTour(userId?: string): boolean {
    const state = this.loadTourState(userId);
    return state?.completed === true || state?.skippedAt !== undefined;
  }

  /**
   * Limpa o estado do tour (para reset ou nova versão)
   */
  public static clearTourState(userId?: string): void {
    try {
      const key = this.getStorageKey(userId);
      localStorage.removeItem(key);
    } catch (error) {
      logger.error("[TourPersistence] Erro ao limpar estado do tour:", error);
    }
  }

  /**
   * Reseta o tour para permitir que seja executado novamente
   */
  public static resetTour(userId?: string): void {
    this.clearTourState(userId);
  }

  /**
   * Migra dados de versões antigas (se necessário)
   */
  public static migrateLegacyData(userId?: string): void {
    try {
      // Verificar se existem dados de versões antigas
      const legacyKeys = [
        "tour_completed",
        "interactive_tour_completed",
        "jess_tour_completed",
      ];

      let foundLegacyData = false;

      for (const legacyKey of legacyKeys) {
        const legacyValue = localStorage.getItem(legacyKey);
        if (legacyValue === "true") {
          foundLegacyData = true;
          break;
        }
      }

      if (foundLegacyData) {
        // Migrar para o novo formato
        this.markTourCompleted(userId);

        // Limpar dados antigos
        legacyKeys.forEach((key) => {
          localStorage.removeItem(key);
        });
      }
    } catch (error) {
      logger.error("[TourPersistence] Erro ao migrar dados legados:", error);
    }
  }

  /**
   * Obtém estatísticas do tour
   */
  public static getTourStats(userId?: string): {
    completed: boolean;
    skipped: boolean;
    currentStep?: number;
    completedSteps?: string[];
    completedAt?: Date;
    skippedAt?: Date;
    version: string;
  } | null {
    const state = this.loadTourState(userId);

    if (!state) {
      return null;
    }

    return {
      completed: state.completed,
      skipped: state.skippedAt !== undefined && !state.completed,
      currentStep: state.currentStep,
      completedSteps: state.completedSteps,
      completedAt: state.completedAt ? new Date(state.completedAt) : undefined,
      skippedAt: state.skippedAt ? new Date(state.skippedAt) : undefined,
      version: state.version,
    };
  }

  /**
   * Verifica se uma nova versão do tour está disponível
   */
  public static isNewVersionAvailable(userId?: string): boolean {
    const state = this.loadTourState(userId);

    if (!state) {
      return true; // Primeira vez
    }

    return state.version !== this.CURRENT_VERSION;
  }

  /**
   * Obtém a versão atual do tour
   */
  public static getCurrentVersion(): string {
    return this.CURRENT_VERSION;
  }

  /**
   * Limpa todos os dados de tour (para manutenção)
   */
  public static clearAllTourData(): void {
    try {
      const keys = Object.keys(localStorage);
      const tourKeys = keys.filter((key) =>
        key.startsWith(this.STORAGE_KEY_PREFIX),
      );

      tourKeys.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Limpar também dados legados
      const legacyKeys = [
        "tour_completed",
        "interactive_tour_completed",
        "jess_tour_completed",
      ];

      legacyKeys.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      logger.error(
        "[TourPersistence] Erro ao limpar todos os dados de tour:",
        error,
      );
    }
  }
}
